import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Stack,
  CardHeader,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  alpha,
  useTheme
} from '@mui/material';
import {
  Notes,
  Add,
  Edit,
  Delete,
  LocationOn
} from '@mui/icons-material';

import { NoteCard } from '../styled/StyledComponents';
import { BREAKPOINTS } from '../constants/dashboardConstants';

const NotesTab = ({ 
  notes, 
  noteDialogOpen, 
  currentNote, 
  onNoteDialogOpen, 
  onNoteDialogClose, 
  onNoteSave,
  onNoteChange 
}) => {
  const theme = useTheme();

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Notes color="primary" />
          Property Investment Notes
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => onNoteDialogOpen()}
          size="large"
          sx={{ borderRadius: 2 }}
        >
          Add New Note
        </Button>
      </Stack>

      <Grid container spacing={3}>
        {notes.map((note) => (
          <Grid item xs={12} md={6} lg={4} key={note.id}>
            <NoteCard elevation={2}>
              <CardHeader
                action={
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Edit Note">
                      <IconButton size="small" onClick={() => onNoteDialogOpen(note)}>
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Note">
                      <IconButton size="small" color="error">
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                }
                title={
                  <Typography variant="h6" noWrap>
                    {note.title}
                  </Typography>
                }
                subheader={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <LocationOn fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {note.propertyAddress}
                    </Typography>
                  </Stack>
                }
              />
              <CardContent>
                <Typography variant="body1" paragraph sx={{ 
                  display: '-webkit-box',
                  overflow: 'hidden',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 3
                }}>
                  {note.content}
                </Typography>
                
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                  {note.tags.map((tag) => (
                    <Chip 
                      key={tag} 
                      label={tag} 
                      size="small" 
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </Stack>
                
                <Typography variant="caption" color="text.secondary">
                  Created: {new Date(note.createdAt).toLocaleDateString()}
                </Typography>
              </CardContent>
            </NoteCard>
          </Grid>
        ))}

        {notes.length === 0 && (
          <Grid item xs={12}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 6, 
                textAlign: 'center', 
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`
              }}
            >
              <Notes sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No notes yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Start adding notes to track your property investment insights
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => onNoteDialogOpen()}
              >
                Add First Note
              </Button>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Floating Action Button for mobile */}
      <Fab
        color="primary"
        aria-label="add note"
        onClick={() => onNoteDialogOpen()}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          display: BREAKPOINTS.MOBILE_FAB_DISPLAY
        }}
      >
        <Add />
      </Fab>

      {/* Enhanced Note Dialog */}
      <Dialog 
        open={noteDialogOpen} 
        onClose={onNoteDialogClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Notes />
          {currentNote.id ? 'Edit Note' : 'Add New Note'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Note Title"
              value={currentNote.title}
              onChange={(e) => onNoteChange({...currentNote, title: e.target.value})}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Property Address"
              value={currentNote.propertyAddress || ''}
              onChange={(e) => onNoteChange({...currentNote, propertyAddress: e.target.value})}
              variant="outlined"
              InputProps={{
                startAdornment: <LocationOn color="action" sx={{ mr: 1 }} />
              }}
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Note Content"
              value={currentNote.content}
              onChange={(e) => onNoteChange({...currentNote, content: e.target.value})}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Tags (comma separated)"
              value={currentNote.tags?.join(', ') || ''}
              onChange={(e) => onNoteChange({...currentNote, tags: e.target.value.split(',').map(tag => tag.trim())})}
              variant="outlined"
              helperText="Add tags to categorize your notes (e.g., investment, analysis, urgent)"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={onNoteDialogClose} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={onNoteSave} 
            variant="contained" 
            startIcon={currentNote.id ? <Edit /> : <Add />}
          >
            {currentNote.id ? 'Update' : 'Save'} Note
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotesTab; 